import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, ChevronRight, Briefcase, FolderOpen, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import type { Organization } from '@/types/organization';

interface LogsOrganizationFiltersProps {
  selected: string[];
  onSelect: (selected: string[]) => void;
  title?: string;
}

interface OrganizationWithGroups {
  id: string;
  name: string;
  nameEn: string;
  sections: Array<{
    id: string;
    name: string;
    nameEn: string;
    groups: Array<{
      id: string;
      name: string;
      nameEn: string;
    }>;
  }>;
}

interface ExpandedState {
  [orgId: string]: boolean;
}

interface SectionExpandedState {
  [sectionId: string]: boolean;
}

export function LogsOrganizationFilters({ selected, onSelect, title }: LogsOrganizationFiltersProps) {
  const [open, setOpen] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationWithGroups[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState<ExpandedState>({});
  const [expandedSections, setExpandedSections] = useState<SectionExpandedState>({});
  const { t, dir, language } = useLanguage();
  const isRTL = dir === 'rtl';

  useEffect(() => {
    const fetchOrganizationsWithGroups = async () => {
      try {
        setLoading(true);
        
        // Fetch organizations
        const orgsQuery = query(collection(db, 'organizations'));
        const orgsSnapshot = await getDocs(orgsQuery);
        const orgsData = orgsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Organization[];
        
        // For each organization, fetch its sections and groups
        const orgsWithGroups: OrganizationWithGroups[] = [];
        
        for (const org of orgsData) {
          // Get sections (parent groups) for this organization
          const sectionsQuery = query(
            collection(db, 'supportGroups'),
            where('organization', '==', org.id),
            where('groupType', '==', 'parent')
          );
          const sectionsSnapshot = await getDocs(sectionsQuery);
          
          const sections = [];
          for (const sectionDoc of sectionsSnapshot.docs) {
            const sectionData = sectionDoc.data();
            
            // Get child groups for this section
            const groupsQuery = query(
              collection(db, 'supportGroups'),
              where('parentGroupId', '==', sectionDoc.id),
              where('organization', '==', org.id)
            );
            const groupsSnapshot = await getDocs(groupsQuery);
            
            const groups = groupsSnapshot.docs.map(groupDoc => {
              const groupData = groupDoc.data();
              return {
                id: groupDoc.id,
                name: groupData.name || '',
                nameEn: groupData.nameEn || groupData.name || ''
              };
            });
            
            sections.push({
              id: sectionDoc.id,
              name: sectionData.name || '',
              nameEn: sectionData.nameEn || sectionData.name || '',
              groups
            });
          }
          
          orgsWithGroups.push({
            id: org.id,
            name: org.name || '',
            nameEn: org.nameEn || org.name || '',
            sections
          });
        }
        
        setOrganizations(orgsWithGroups);
      } catch (error) {
        console.error('Error fetching organizations with groups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationsWithGroups();
  }, []);

  const handleSelect = (value: string, checked: boolean) => {
    if (checked) {
      // Add the selected item
      const newSelected = [...selected, value];
      
      // If selecting an organization, also select all its sections and groups
      if (!value.startsWith('section_') && !value.startsWith('group_')) {
        const org = organizations.find(o => o.id === value);
        if (org) {
          // Add all sections
          org.sections.forEach(section => {
            if (!newSelected.includes(`section_${section.id}`)) {
              newSelected.push(`section_${section.id}`);
            }
            // Add all groups in this section
            section.groups.forEach(group => {
              if (!newSelected.includes(`group_${group.id}`)) {
                newSelected.push(`group_${group.id}`);
              }
            });
          });
        }
      }
      // If selecting a section, also select all its groups
      else if (value.startsWith('section_')) {
        const sectionId = value.replace('section_', '');
        const org = organizations.find(o => o.sections.some(s => s.id === sectionId));
        if (org) {
          const section = org.sections.find(s => s.id === sectionId);
          if (section) {
            // Add all groups in this section
            section.groups.forEach(group => {
              if (!newSelected.includes(`group_${group.id}`)) {
                newSelected.push(`group_${group.id}`);
              }
            });
          }
        }
      }
      
      onSelect(newSelected);
    } else {
      // Remove the selected item
      const newSelected = selected.filter(item => item !== value);
      
      // If deselecting an organization, also deselect all its sections and groups
      if (!value.startsWith('section_') && !value.startsWith('group_')) {
        const org = organizations.find(o => o.id === value);
        if (org) {
          // Remove all sections
          org.sections.forEach(section => {
            newSelected.splice(newSelected.indexOf(`section_${section.id}`), 1);
            // Remove all groups in this section
            section.groups.forEach(group => {
              newSelected.splice(newSelected.indexOf(`group_${group.id}`), 1);
            });
          });
        }
      }
      // If deselecting a section, also deselect all its groups
      else if (value.startsWith('section_')) {
        const sectionId = value.replace('section_', '');
        const org = organizations.find(o => o.sections.some(s => s.id === sectionId));
        if (org) {
          const section = org.sections.find(s => s.id === sectionId);
          if (section) {
            // Remove all groups in this section
            section.groups.forEach(group => {
              newSelected.splice(newSelected.indexOf(`group_${group.id}`), 1);
            });
          }
        }
      }
      
      onSelect(newSelected);
    }
  };

  const getOrganizationDisplayName = (org: OrganizationWithGroups) => {
    if (language === 'ar') {
      return org.name || org.nameEn || 'Unknown Organization';
    } else {
      return org.nameEn || org.name || 'Unknown Organization';
    }
  };

  const getSectionDisplayName = (section: { name: string; nameEn: string }) => {
    if (language === 'ar') {
      return section.name || section.nameEn || 'Unknown Section';
    } else {
      return section.nameEn || section.name || 'Unknown Section';
    }
  };

  const getGroupDisplayName = (group: { name: string; nameEn: string }) => {
    if (language === 'ar') {
      return group.name || group.nameEn || 'Unknown Group';
    } else {
      return group.nameEn || group.name || 'Unknown Group';
    }
  };

  const toggleExpanded = (orgId: string) => {
    setExpandedOrgs(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }));
  };

  const toggleSectionExpanded = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const selectedCount = selected.length;
  const displayText = selectedCount === 0 
    ? title || t('filters.organizationsTitle')
    : selectedCount === 1
    ? getOrganizationDisplayName(organizations.find(org => org.id === selected[0]) || {} as OrganizationWithGroups)
    : `${selectedCount} ${t('filters.selected')}`;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className={`h-10 w-full ${isRTL ? 'flex-row-reverse' : ''}`}
          disabled={loading}
        >
          {loading ? t('common.loading') : displayText}
          <ChevronDown className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
        </Button>
      </DropdownMenuTrigger>
             <DropdownMenuContent 
         align={isRTL ? "start" : "end"} 
         className={`w-80 max-h-[326px] overflow-y-auto ${isRTL ? 'text-right' : 'text-left'}`}
       >
         {organizations.map((org) => (
           <div key={org.id}>
                                         <div className="flex items-center">
                {org.sections.length > 0 && (
                  <button
                    onClick={() => toggleExpanded(org.id)}
                    className={`p-1 hover:bg-accent rounded-sm ${isRTL ? 'order-last' : 'order-first'}`}
                  >
                    {expandedOrgs[org.id] ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                )}
                
                <DropdownMenuCheckboxItem
                  checked={selected.includes(org.id)}
                  onCheckedChange={(checked) => handleSelect(org.id, checked)}
                  className={`${isRTL ? 'text-right' : 'text-left'} font-medium flex-1`}
                  dir={dir}
                >
                  <Briefcase className={`h-4 w-4 ${isRTL ? 'ml-3' : 'mr-2'}`} />
                  {getOrganizationDisplayName(org)}
                </DropdownMenuCheckboxItem>
              </div>
             
             {org.sections.length > 0 && expandedOrgs[org.id] && (
               <div className="ml-4">
                                   {org.sections.map((section) => (
                    <div key={section.id}>
                                             <div className="flex items-center">
                         {section.groups.length > 0 && (
                           <button
                             onClick={() => toggleSectionExpanded(section.id)}
                             className={`p-1 hover:bg-accent rounded-sm ${isRTL ? 'order-last' : 'order-first'}`}
                           >
                             {expandedSections[section.id] ? (
                               <ChevronDown className="h-4 w-4" />
                             ) : (
                               <ChevronRight className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                             )}
                           </button>
                         )}
                         
                         <DropdownMenuCheckboxItem
                           checked={selected.includes(`section_${section.id}`)}
                           onCheckedChange={(checked) => handleSelect(`section_${section.id}`, checked)}
                           className={`${isRTL ? 'text-right' : 'text-left'} text-sm flex-1`}
                           dir={dir}
                         >
                           <FolderOpen className={`h-4 w-4 ${isRTL ? 'ml-3' : 'mr-2'}`} />
                           {getSectionDisplayName(section)}
                         </DropdownMenuCheckboxItem>
                       </div>
                      
                      {section.groups.length > 0 && expandedSections[section.id] && (
                        <div className="ml-4">
                          {section.groups.map((group) => (
                            <DropdownMenuCheckboxItem
                              key={group.id}
                              checked={selected.includes(`group_${group.id}`)}
                              onCheckedChange={(checked) => handleSelect(`group_${group.id}`, checked)}
                              className={`${isRTL ? 'text-right' : 'text-left'} text-xs`}
                              dir={dir}
                            >
                              <Users className={`h-4 w-4 ${isRTL ? 'ml-3' : 'mr-2'}`} />
                              {getGroupDisplayName(group)}
                            </DropdownMenuCheckboxItem>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
               </div>
             )}
             
             {organizations.length > 1 && <DropdownMenuSeparator />}
           </div>
         ))}
       </DropdownMenuContent>
    </DropdownMenu>
  );
}
